/**
 * ENG-7833: host-side CLI version pinning.
 *
 * `AGT_CLI_PIN_VERSION=<version>` freezes a host on an exact `agt-cli`
 * version. Pin wins: the manager installs that exact version (including a
 * downgrade) and, once matched, stops moving the host forward. Neither the
 * `latest`/channel poll nor the `urgent` dist-tag escape hatch overrides a
 * valid pin. The one opt-out is `AGT_CLI_PIN_ALLOW_URGENT=1`, which lets an
 * emergency `urgent` hotfix land over the pin while still holding the host on
 * the pin for ordinary `latest` movement.
 *
 * The pin value is materialized into the host environment (bootstrap persists
 * it to `/etc/environment`, the systemd unit carries it, and the manager reads
 * it here), mirroring how `AGT_CLI_RELEASE_CHANNEL` already works. It is a
 * value, not an `_ENABLED`/`_DISABLED`/`_MODE` gate, so it is deliberately a
 * plain env var rather than a registry flag (a per-host fleet flag is tracked
 * as a follow-up).
 *
 * Everything here is a pure function of its inputs so the decision logic can be
 * unit-tested without a live registry or a real install.
 */

/**
 * A pin must look like a concrete semver (optionally prerelease/build tagged).
 * We validate strictly rather than pass an arbitrary string to `npm install
 * -g @integrity-labs/agt-cli@<pin>`: a garbage pin would otherwise make every
 * poll attempt an install of a nonexistent version and fail in a tight loop.
 * An invalid pin is treated as "not pinned" by the caller (with a warning) so a
 * typo never silently freezes a host on nothing.
 */
export function isValidPinVersion(v: string): boolean {
  return PIN_VERSION_RE.test(v);
}

/**
 * Strict SemVer 2.0.0 (semver.org's official regex, anchored). Deliberately
 * NOT the loose `\d+\.\d+\.\d+` form: that accepts leading-zero components
 * (`01.2.3`) and empty prerelease segments (`1.2.3-..`, `1.2.3-alpha..1`),
 * which npm rejects but which would still reach `npm install -g …@<pin>` and
 * loop on failure. `packages/api/src/lib/host-bootstrap.ts` MUST keep the same
 * pattern (it can't import this CLI module across packages); change both.
 */
export const PIN_VERSION_RE =
  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?$/;

/** True when the operator set a non-blank `AGT_CLI_PIN_VERSION`. */
export function isPinSet(raw: string | undefined): boolean {
  return typeof raw === 'string' && raw.trim() !== '';
}

/**
 * ENG-7853: resolve the effective pin source from the two possible inputs. The
 * explicit host env var (`AGT_CLI_PIN_VERSION`, set at bootstrap or over SSH) is
 * the highest-precedence per-host override; when it is unset (or blank), fall
 * back to the DB-driven pin delivered on the heartbeat (set from the console).
 * The returned value still flows through {@link decidePin}, so a garbage value
 * from either source is ignored with a warning rather than looping on a
 * nonexistent install. Pure so the precedence is unit-testable.
 */
export function resolveEffectivePinRaw(
  envPin: string | undefined,
  dbPin: string | null | undefined,
): string | undefined {
  if (isPinSet(envPin)) return envPin;
  return dbPin ?? undefined;
}

/**
 * Normalize a raw `AGT_CLI_PIN_VERSION` to a concrete version string, or null
 * when it is unset, blank, or not a valid version. Strips an optional leading
 * `v` so both `0.28.330` and `v0.28.330` work.
 */
export function normalizePinVersion(raw: string | undefined): string | null {
  if (!isPinSet(raw)) return null;
  const v = raw!.trim().replace(/^v/, '');
  return isValidPinVersion(v) ? v : null;
}

/**
 * True when `AGT_CLI_PIN_ALLOW_URGENT` opts a pinned host back into taking an
 * `urgent` dist-tag hotfix over the pin. Accepts the usual truthy spellings.
 */
export function pinAllowsUrgent(raw: string | undefined): boolean {
  const v = (raw ?? '').trim().toLowerCase();
  return v === '1' || v === 'true' || v === 'yes' || v === 'on';
}

export type PinDecision =
  /** No valid pin in effect; the caller runs its normal channel logic. */
  | { kind: 'unpinned' }
  /** Pin set but not a valid version; warn once, then behave as unpinned. */
  | { kind: 'invalid'; raw: string }
  /** Installed version already equals the pin; nothing to do. */
  | { kind: 'satisfied'; version: string }
  /** Install this exact version (may be a downgrade). */
  | { kind: 'install'; version: string };

/**
 * The pin decision, given the installed version and the two env vars. The
 * urgent opt-out is handled separately by the caller (it needs a network fetch
 * of the `urgent` tag), so this function only decides pin vs. install vs.
 * satisfied; a `satisfied`/`install` result still lets the caller consult
 * `urgent` first when {@link pinAllowsUrgent} is true.
 */
export function decidePin(opts: {
  installed: string;
  pinRaw: string | undefined;
}): PinDecision {
  const { installed, pinRaw } = opts;
  if (!isPinSet(pinRaw)) return { kind: 'unpinned' };
  const pinned = normalizePinVersion(pinRaw);
  if (!pinned) return { kind: 'invalid', raw: pinRaw!.trim() };
  if (installed === pinned) return { kind: 'satisfied', version: pinned };
  return { kind: 'install', version: pinned };
}
